import React, { useState } from 'react';
import { usePIM } from '../../context/PIMContext';
import { Modal } from '../ui/Modal';
import { Input } from '../ui/Input';
import { PrimaryButton } from '../ui/PrimaryButton';
import { Trash, Plus, Pencil, Check, X } from 'lucide-react';
import { Badge } from '../ui/Badge';
import { useSortableData } from '../../hooks/useSortableData';
import { SortableHeader } from '../ui/SortableHeader';

export const LanguageManagementModal = ({ isOpen, onClose }) => {
    const {
        availableLanguages, languageGroups,
        addMasterLanguage, deleteMasterLanguage, updateMasterLanguage,
        addLanguageGroup, deleteLanguageGroup, updateLanguageGroup
    } = usePIM();

    const [activeTab, setActiveTab] = useState('languages'); // 'languages' | 'groups'

    // --- LANGUAGES STATE ---
    const [newName, setNewName] = useState('');
    const [newCode, setNewCode] = useState('');
    const [editingLangId, setEditingLangId] = useState(null);
    const [editLangData, setEditLangData] = useState({ name: '', code: '' });

    // --- GROUPS STATE ---
    const [newGroupName, setNewGroupName] = useState('');
    const [newGroupColor, setNewGroupColor] = useState('#000000');
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editGroupData, setEditGroupData] = useState({ name: '', color: '' });

    // --- LANGUAGE HANDLERS ---
    const handleAddLanguage = async () => {
        if (!newName || !newCode) return;
        await addMasterLanguage({ name: newName, code: newCode });
        setNewName('');
        setNewCode('');
    };

    const startEditLanguage = (lang) => {
        setEditingLangId(lang.id);
        setEditLangData({ name: lang.name, code: lang.code });
    };

    const saveEditLanguage = async (id) => {
        await updateMasterLanguage(id, editLangData);
        setEditingLangId(null);
    };

    // --- GROUP HANDLERS ---
    const handleAddGroup = async () => {
        if (!newGroupName) return;
        await addLanguageGroup({ name: newGroupName, color: newGroupColor });
        setNewGroupName('');
        setNewGroupColor('#000000');
    };

    const startEditGroup = (group) => {
        setEditingGroupId(group.id);
        setEditGroupData({ name: group.name, color: group.color || '#000000' });
    };

    const saveEditGroup = async (id) => {
        await updateLanguageGroup(id, editGroupData);
        setEditingGroupId(null);
    };

    // --- SORTING ---
    const { items: sortedLanguages, requestSort: requestSortLanguages, sortConfig: sortConfigLanguages } = useSortableData(availableLanguages);
    const { items: sortedGroups, requestSort: requestSortGroups, sortConfig: sortConfigGroups } = useSortableData(languageGroups);

    return (
        <Modal isOpen={isOpen} onClose={onClose} title="Manage Languages" maxWidth="max-w-4xl">
            <div className="flex flex-col h-[600px]">
                {/* Top Tabs */}
                <div className="flex justify-between items-center border-b border-stone-700 mb-4">
                    <div className="flex">
                        <button
                            onClick={() => setActiveTab('languages')}
                            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${activeTab === 'languages' ? 'border-blue-500 text-white' : 'border-transparent text-stone-400 hover:text-stone-200'}`}
                        >
                            Languages
                        </button>
                        <button
                            onClick={() => setActiveTab('groups')}
                            className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 ${activeTab === 'groups' ? 'border-blue-500 text-white' : 'border-transparent text-stone-400 hover:text-stone-200'}`}
                        >
                            Groups
                        </button>
                    </div>
                </div>

                {/* Content Area */}
                <div className="flex-1 overflow-hidden flex flex-col">

                    {/* --- LANGUAGES TAB --- */}
                    {activeTab === 'languages' && (
                        <div className="flex flex-col h-full">
                            {/* Add Language Form */}
                            <div className="bg-stone-900 p-4 rounded-lg border border-stone-700 mb-4 grid grid-cols-12 gap-3 items-end shrink-0">
                                <div className="col-span-4 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Name</label>
                                    <Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Language Name" />
                                </div>
                                <div className="col-span-5 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Code</label>
                                    <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="ISO Code" />
                                </div>
                                <div className="col-span-3">
                                    <PrimaryButton onClick={handleAddLanguage} className="w-full justify-center h-[38px]"><Plus size={16} /></PrimaryButton>
                                </div>
                            </div>

                            {/* Language List */}
                            <div className="flex-1 overflow-y-auto">
                                <table className="w-full text-left border-collapse">
                                    <thead className="text-xs text-stone-500 uppercase bg-stone-950 sticky top-0 z-10">
                                        <tr>
                                            <SortableHeader label="Name" sortKey="name" currentSort={sortConfigLanguages} onSort={requestSortLanguages} />
                                            <SortableHeader label="Code" sortKey="code" currentSort={sortConfigLanguages} onSort={requestSortLanguages} />
                                            <th className="px-3 py-2 border-b border-stone-800 text-right">Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody className="text-sm">
                                        {sortedLanguages.map(lang => (
                                            <tr key={lang.id} className="border-b border-stone-800 hover:bg-stone-800/20">
                                                {editingLangId === lang.id ? (
                                                    <>
                                                        <td className="px-3 py-2"><Input value={editLangData.name} onChange={(e) => setEditLangData({ ...editLangData, name: e.target.value })} className="h-8 text-xs" /></td>
                                                        <td className="px-3 py-2"><Input value={editLangData.code} onChange={(e) => setEditLangData({ ...editLangData, code: e.target.value })} className="h-8 text-xs" /></td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => saveEditLanguage(lang.id)} className="text-green-500 p-1 hover:bg-stone-800 rounded"><Check size={14} /></button>
                                                                <button onClick={() => setEditingLangId(null)} className="text-stone-500 p-1 hover:bg-stone-800 rounded"><X size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                ) : (
                                                    <>
                                                        <td className="px-3 py-2 text-stone-300">{lang.name}</td>
                                                        <td className="px-3 py-2 text-stone-400 font-mono">{lang.code}</td>
                                                        <td className="px-3 py-2 text-right">
                                                            <div className="flex justify-end gap-1">
                                                                <button onClick={() => startEditLanguage(lang)} className="text-stone-500 hover:text-blue-400 p-1 hover:bg-stone-800 rounded"><Pencil size={14} /></button>
                                                                <button onClick={() => deleteMasterLanguage(lang.id)} className="text-stone-500 hover:text-red-400 p-1 hover:bg-stone-800 rounded"><Trash size={14} /></button>
                                                            </div>
                                                        </td>
                                                    </>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* --- GROUPS TAB --- */}
                    {activeTab === 'groups' && (
                        <div className="flex flex-col h-full">
                            {/* Add Group Form */}
                            <div className="bg-stone-900 p-4 rounded-lg border border-stone-700 mb-4 flex gap-4 items-end shrink-0">
                                <div className="flex-1 space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Group Name</label>
                                    <Input value={newGroupName} onChange={(e) => setNewGroupName(e.target.value)} placeholder="e.g. European, Asian" />
                                </div>
                                <div className="space-y-1">
                                    <label className="text-xs font-bold text-stone-500 uppercase">Color</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            value={newGroupColor}
                                            onChange={(e) => setNewGroupColor(e.target.value)}
                                            className="h-[38px] w-[60px] bg-transparent border border-stone-700 rounded cursor-pointer"
                                        />
                                    </div>
                                </div>
                                <PrimaryButton onClick={handleAddGroup} className="h-[38px] px-4"><Plus size={16} /> Add</PrimaryButton>
                            </div>

                            {/* Group List */}
                            <div className="flex-1 overflow-y-auto space-y-2">
                                {sortedGroups.map((group) => (
                                    <div key={group.id} className="flex items-center justify-between bg-stone-800/50 p-3 rounded border border-stone-700/50 hover:border-stone-600 transition">
                                        {editingGroupId === group.id ? (
                                            <div className="flex-1 flex gap-4 items-center mr-2">
                                                <Input
                                                    value={editGroupData.name}
                                                    onChange={(e) => setEditGroupData({ ...editGroupData, name: e.target.value })}
                                                    className="h-8 text-xs flex-1"
                                                />
                                                <div className="flex gap-1">
                                                    <input
                                                        type="color"
                                                        value={editGroupData.color}
                                                        onChange={(e) => setEditGroupData({ ...editGroupData, color: e.target.value })}
                                                        className="h-8 w-12 bg-transparent border border-stone-700 rounded cursor-pointer"
                                                    />
                                                </div>
                                            </div>
                                        ) : (
                                            <div className="flex items-center gap-3">
                                                <div className={`w-3 h-3 rounded-full`} style={{ backgroundColor: group.color || 'gray' }} />
                                                <div className="text-stone-200 font-medium text-sm">{group.name}</div>
                                            </div>
                                        )}

                                        <div className="flex items-center gap-1 shrink-0">
                                            {editingGroupId === group.id ? (
                                                <>
                                                    <button onClick={() => saveEditGroup(group.id)} className="text-green-500 hover:text-green-400 p-2 rounded hover:bg-stone-700/50 transition"><Check size={14} /></button>
                                                    <button onClick={() => setEditingGroupId(null)} className="text-stone-500 hover:text-stone-400 p-2 rounded hover:bg-stone-700/50 transition"><X size={14} /></button>
                                                </>
                                            ) : (
                                                <>
                                                    <button onClick={() => startEditGroup(group)} className="text-stone-500 hover:text-blue-400 p-2 rounded hover:bg-stone-700/50 transition"><Pencil size={14} /></button>
                                                    <button onClick={() => deleteLanguageGroup(group.id)} className="text-stone-500 hover:text-red-400 p-2 rounded hover:bg-stone-700/50 transition"><Trash size={14} /></button>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};
